<template>
  <modal @save="submit" @back="goBack">
    <template #header>{{ $t('reviewSubmit.modal.title.editBusinessInfo') }}</template>
    <form autocomplete="off">
      <input-select
        id="legal-entity-type"
        class="legalEntityType"
        v-model="business.legalEntityType"
        :defaultValue="$t('select')"
        :options="legalEntityTypeOptions"
        v-validation:closed="[['dynamic', '/business/legalEntityType']]"
        @change="formCleanUp"
        :disabled="hasParenApplicationtId"
      >
        <template #label>{{ $t('businessInfo.legalEntityType.label') }}</template>
      </input-select>

      <combined-element v-if="!!business.legalEntityType">
        <template v-if="business.legalEntityType === TRUST">
          <abr-search
            key="business-name-trust"
            id="business-name-trust"
            class="businessName"
            :value="business.legalBusinessName"
            :name="business.legalBusinessName"
            :maxlength="BUSINESS_NAME_MAX_LENGTH"
            :acn="false"
            :placeholder="$t('businessInfo.businessNamePlaceholder')"
            :abnTypeLabel="$t('businessInfo.abn.label')"
            :acnTypeLabel="$t('businessInfo.acn.label')"
            :label="$t('businessInfo.trustNameLabel')"
            @change="onOptionChange"
            @input="business = { ...business, legalBusinessName: $event }"
            v-validation="[['dynamic', '/business/_extension/trustName']]"
            :disabled="hasParenApplicationtId"
          ></abr-search>
          <input-text-masked
            key="business-abn-trust"
            id="business-abn-trust"
            name="business-abn-trust"
            v-model="business.abnTaxId"
            :mask="[
              /\d/,
              /\d/,
              ' ',
              /\d/,
              /\d/,
              /\d/,
              ' ',
              /\d/,
              /\d/,
              /\d/,
              ' ',
              /\d/,
              /\d/,
              /\d/,
            ]"
            :cleanup-pattern="/[-|\s|(|)]/g"
            class="halved"
            :placeholder="$t('businessInfo.abn.placeholder')"
            :maxlength="ABN_MAX_LENGTH"
            :strict-mode="{ regex: /[^0-9]/g, skipDefault: true }"
            v-validation="[['dynamic', '/business/_extension/trustAbn']]"
            ref="abnTaxId"
            :disabled="hasParenApplicationtId"
          >
            <template #label>
              {{ $t('businessInfo.abn.trustLabel') }}
              <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
                <i18n path="businessInfo.abn.tooltip">
                  <brand-link class="descriptionLink" target="_blank" :href="abnLink" slot="link">{{
                    $t('businessInfo.abn.linkText')
                  }}</brand-link>
                </i18n>
              </popover>
            </template>
          </input-text-masked>
          <input-text
            key="trust-trading-name"
            id="trust-trading-name"
            name="trust-trading-name"
            v-model="business.doingBusinessAs"
            :maxlength="DOING_BUSINESS_AS_MAX_LENGTH"
            v-validation="[['dynamic', '/business/doingBusinessAs']]"
            :disabled="hasParenApplicationtId"
          >
            <template #label>{{ $t('businessInfo.trustDoingBusinessAs.label') }}</template>
            <template #description>
              {{ $t('businessInfo.trustDoingBusinessAs.suffix') }}
            </template>
          </input-text>
          <input-inline>
            <input-select
              id="trustee-type"
              class="trusteeType"
              ref="trusteeType"
              v-model="business._extension.trusteeType"
              :defaultValue="$t('select')"
              :options="trusteeTypeOptions"
              :disabled="hasParenApplicationtId"
              v-validation:closed="[['dynamic', '/business/_extension/trusteeType']]"
              @change="trusteeFormCleanUp"
            >
              <template #label>{{ $t('businessInfo.trusteeType.label') }}</template>
            </input-select>
          </input-inline>

          <combined-element
            class="deepCombined"
            v-if="business._extension.trusteeType === TRUSTEE_COMPANY"
          >
            <abr-search
              key="trustee-name-company"
              id="trustee-name-company"
              class="trusteeName"
              :value="_extension.trusteeName"
              :placeholder="$t('businessInfo.businessNamePlaceholder')"
              :abnTypeLabel="$t('businessInfo.abn.trusteeLabel')"
              :acnTypeLabel="$t('businessInfo.acn.trusteeLabel')"
              :label="$t('businessInfo.trusteeNameLabel.COMPANY')"
              :acn="true"
              :maxlength="TRUSTEE_NAME_MAX_LENGTH"
              @change="onOptionChangeAcnTrustee"
              @input="_extension = { ..._extension, trusteeName: $event }"
              v-validation="[['dynamic', '/business/_extension/trusteeName']]"
              :disabled="hasParenApplicationtId"
            ></abr-search>
            <input-text-masked
              key="trustee-acn"
              id="trustee-acn"
              name="trustee-acn"
              v-model="business._extension.trusteeTaxId"
              class="halved"
              :mask="[/\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/]"
              :cleanup-pattern="/[-|\s|(|)]/g"
              :placeholder="$t('businessInfo.acn.placeholder')"
              :maxlength="ACN_MAX_LENGTH"
              :strict-mode="{ regex: /[^0-9]/g, skipDefault: true }"
              v-validation="[['dynamic', '/business/_extension/trusteeAcn']]"
              ref="trusteeTaxId"
              :disabled="hasParenApplicationtId"
            >
              <template #label>
                {{ $t('businessInfo.acn.trusteeLabel') }}
                <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
                  <i18n path="businessInfo.acn.tooltip">
                    <brand-link
                      class="descriptionLink"
                      target="_blank"
                      :href="acnLink"
                      slot="link"
                      >{{ $t('businessInfo.acn.linkText') }}</brand-link
                    >
                  </i18n>
                </popover>
              </template>
            </input-text-masked>
            <input-text
              key="trustee-another-business-name"
              id="trustee-another-business-name"
              name="trustee-another-business-name"
              ref="trusteeAnotherBusinessName"
              v-model="business._extension.trusteeDoingBusinessAs"
              :maxlength="DOING_BUSINESS_AS_MAX_LENGTH"
              v-validation="[['dynamic', '/business/_extension/trusteeDoingBusinessAs']]"
              :disabled="hasParenApplicationtId"
            >
              <template #label>{{ $t('businessInfo.trusteeDoingBusinessAs.label') }}</template>
              <template #description>
                {{ $t('businessInfo.trusteeDoingBusinessAs.suffix') }}
              </template>
            </input-text>
          </combined-element>
        </template>

        <template v-else-if="business.legalEntityType === COMPANY">
          <abr-search
            key="business-name-company"
            id="business-name-company"
            class="businessName"
            :value="business.legalBusinessName"
            :acn="true"
            :maxlength="BUSINESS_NAME_MAX_LENGTH"
            :placeholder="$t('businessInfo.businessNamePlaceholder')"
            :abnTypeLabel="$t('businessInfo.abn.label')"
            :acnTypeLabel="$t('businessInfo.acn.label')"
            :label="$t('businessInfo.businessNameLabel')"
            @change="onOptionChangeAcn"
            @input="business = { ...business, legalBusinessName: $event }"
            v-validation="[['dynamic', '/business/legalBusinessName']]"
            :disabled="hasParenApplicationtId"
          ></abr-search>
          <input-inline>
            <input-text-masked
              key="business-acn"
              id="business-acn"
              name="business-acn"
              v-model="business.taxId"
              :mask="[/\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/]"
              :cleanup-pattern="/[-|\s|(|)]/g"
              :placeholder="$t('businessInfo.acn.placeholder')"
              :maxlength="ACN_MAX_LENGTH"
              :strict-mode="{ regex: /[^0-9]/g, skipDefault: true }"
              v-validation="[['dynamic', '/business/_extension/acn']]"
              ref="taxId"
              :disabled="hasParenApplicationtId"
            >
              <template #label>
                {{ $t('businessInfo.acn.label') }}
                <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
                  <i18n path="businessInfo.acn.tooltip">
                    <brand-link
                      class="descriptionLink"
                      target="_blank"
                      :href="acnLink"
                      slot="link"
                      >{{ $t('businessInfo.acn.linkText') }}</brand-link
                    >
                  </i18n>
                </popover>
              </template>
            </input-text-masked>
          </input-inline>
        </template>
        <template v-else>
          <abr-search
            :key="`business-name-${business.legalEntityType}`"
            :id="`business-name-${business.legalEntityType}`"
            :name="business.legalBusinessName"
            class="businessName"
            :value="business.legalBusinessName"
            :placeholder="$t('businessInfo.businessNamePlaceholder')"
            :acn="false"
            :abn="business.abnTaxId"
            :abnTypeLabel="$t('businessInfo.abn.label')"
            :acnTypeLabel="$t('businessInfo.acn.label')"
            :label="$t('businessInfo.businessNameLabel')"
            :maxlength="BUSINESS_NAME_MAX_LENGTH"
            @change="onOptionChange"
            @input="business = { ...business, legalBusinessName: $event }"
            v-validation="[['dynamic', '/business/legalBusinessName']]"
            :disabled="hasParenApplicationtId"
          ></abr-search>
          <input-inline>
            <input-text-masked
              key="business-abn"
              id="business-abn"
              name="business-abn"
              v-model="business.abnTaxId"
              :mask="[
                /\d/,
                /\d/,
                ' ',
                /\d/,
                /\d/,
                /\d/,
                ' ',
                /\d/,
                /\d/,
                /\d/,
                ' ',
                /\d/,
                /\d/,
                /\d/,
              ]"
              :cleanup-pattern="/[-|\s|(|)]/g"
              :placeholder="$t('businessInfo.abn.placeholder')"
              :maxlength="ABN_MAX_LENGTH"
              :strict-mode="{ regex: /[^0-9]/g, skipDefault: true }"
              v-validation="[['dynamic', '/business/_extension/abn']]"
              ref="taxId"
              :disabled="hasParenApplicationtId"
            >
              <template #label>
                {{ $t('businessInfo.abn.label') }}
                <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
                  <i18n path="businessInfo.abn.tooltip">
                    <brand-link
                      class="descriptionLink"
                      target="_blank"
                      :href="abnLink"
                      slot="link"
                      >{{ $t('businessInfo.abn.linkText') }}</brand-link
                    >
                  </i18n>
                </popover>
              </template>
            </input-text-masked>
          </input-inline>
        </template>
        <input-text
          v-if="business.legalEntityType !== TRUST"
          key="another-business-name"
          id="another-business-name"
          name="another-business-name"
          ref="anotherBusinessName"
          v-model="business.doingBusinessAs"
          :maxlength="DOING_BUSINESS_AS_MAX_LENGTH"
          v-validation="[['dynamic', '/business/doingBusinessAs']]"
          :disabled="hasParenApplicationtId"
        >
          <template #label>{{ $t('businessInfo.doingBusinessAsLabel.label') }}</template>
          <template #description>
            {{ $t('businessInfo.doingBusinessAsLabel.suffix') }}
          </template>
        </input-text>
      </combined-element>

      <address-autocomplete
        id="business-address"
        ref="businessAddress"
        labelPathReference="businessInfo"
        v-model="business"
        validationPathPrefix="/business"
        :maxlength="ADDRESS_MAX_LENGTH"
        :postalCodePlaceholder="$t('postalCodePlaceholder')"
        :stateDefaultValue="$t('select')"
        @autocomplete="onPlaceChange"
      >
        <template #phone>
          <input-text-masked
            id="business-phone"
            ref="businessPhone"
            name="business-phone"
            v-model="business.phone"
            :maxlength="PHONE_MAX_LENGTH"
            v-validation="[['dynamic', '/business/phone']]"
            :mask="phoneMask"
            :cleanup-pattern="/[-|\s|(|)]/g"
            :placeholder-char="'\u2000'"
            :guide="false"
          >
            <template #label>{{ $t('businessInfo.phoneFieldLabel') }}</template>
          </input-text-masked>
        </template>
      </address-autocomplete>
    </form>
  </modal>
</template>

<script>
import Modal from '@/components/Modal';
import abrSearch from '@/components/abrSearch';
import InputInline from '@/components/InputInline';
import CombinedElement from '@/components/CombinedElement';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import modalMixin from '@/mixins/modal';
import {
  BUSINESS_NAME_MAX_LENGTH,
  ACN_MAX_LENGTH,
  ABN_MAX_LENGTH,
  DOING_BUSINESS_AS_MAX_LENGTH,
  TRUSTEE_NAME_MAX_LENGTH,
  ADDRESS_MAX_LENGTH,
  CITY_MAX_LENGTH,
  POSTAL_CODE_MAX_LENGTH,
  PHONE_MAX_LENGTH,
} from '@/modules/validation-service/fields-length';
import states from '@/modules/states';
import legalEntityTypes, { COMPANY, TRUST } from '@/modules/legal-entity-types';
import trusteeTypes, {
  COMPANY as TRUSTEE_COMPANY,
  PARTNERSHIP as TRUSTEE_PARTNERSHIP,
} from '@/modules/trustee-types';
import mapConstsToOptions from '@/utils/map-consts-to-options';
import { getPhoneMask } from '@/modules/field-mask';

export default {
  name: 'edit-business-info',
  components: {
    Modal,
    InputInline,
    abrSearch,
    CombinedElement,
    AddressAutocomplete,
  },
  data() {
    return {
      abnLink: this.$env.VUE_APP_ABN_URL,
      acnLink: this.$env.VUE_APP_ACN_URL,
      businessNameOptions: [],
      trusteeNameOptions: [],

      COMPANY,
      TRUST,
      TRUSTEE_COMPANY,
      TRUSTEE_PARTNERSHIP,

      BUSINESS_NAME_MAX_LENGTH,
      ACN_MAX_LENGTH,
      ABN_MAX_LENGTH,
      DOING_BUSINESS_AS_MAX_LENGTH,
      TRUSTEE_NAME_MAX_LENGTH,
      ADDRESS_MAX_LENGTH,
      CITY_MAX_LENGTH,
      POSTAL_CODE_MAX_LENGTH,
      PHONE_MAX_LENGTH,
    };
  },
  computed: {
    business: {
      get() {
        return this.application.business;
      },
      set(value) {
        this.application = { ...this.application, business: value };
      },
    },
    _extension: {
      get() {
        return this.business._extension;
      },
      set(value) {
        this.business = { ...this.business, _extension: value };
      },
    },
    legalEntityTypeOptions() {
      return mapConstsToOptions(
        Object.values(legalEntityTypes),
        'businessInfo.legalEntityType.options',
      );
    },
    trusteeTypeOptions() {
      return mapConstsToOptions(Object.values(trusteeTypes), 'businessInfo.trusteeType.options');
    },
    stateOptions() {
      return mapConstsToOptions(states, 'states');
    },
    legalBusinessName() {
      const tag = this.business.legalEntityType === TRUST ? 'trustNameLabel' : 'businessNameLabel';
      return this.$t(`businessInfo.${tag}`);
    },
    phoneMask() {
      return getPhoneMask(this.business.phone);
    },
    hasParenApplicationtId() {
      return !!this.application._extension.parentApplicationId;
    },
  },
  methods: {
    formCleanUp() {
      this.business.taxId = undefined;
      this.businessCopy.abnTaxId = undefined;
      this.business.legalBusinessName = undefined;
      this.business.doingBusinessAs = undefined;
      this.business._extension.trusteeTaxId = undefined;
      this.business._extension.trusteeDoingBusinessAs = undefined;
      this.business._extension.trusteeName = undefined;
    },
    trusteeFormCleanUp() {
      this.business._extension.trusteeTaxId = undefined;
      this.business._extension.trusteeDoingBusinessAs = undefined;
      this.business._extension.trusteeName = undefined;
    },
    setFocusTo(element) {
      if (element) {
        this.$nextTick().then(() => {
          element.focus();
        });
      }
    },
    onOptionChange(option) {
      this.business = {
        ...this.business,
        legalBusinessName: option.value,
        abnTaxId: option.abn,
      };
      this.$refs.taxId.setErrorMessage();

      this.setFocusTo(
        this.business.legalEntityType === TRUST
          ? this.$refs.trusteeType.$refs.trigger
          : this.$refs.anotherBusinessName.$refs.nativeInput,
      );
    },
    onOptionChangeAcn(option) {
      this.business = {
        ...this.business,
        legalBusinessName: option.value,
        taxId: option.acn,
      };
      this.$refs.taxId.setErrorMessage();

      this.setFocusTo(this.$refs.anotherBusinessName.$refs.nativeInput);
    },
    onOptionChangeTrustee(option) {
      this.business = {
        ...this.business,
        _extension: {
          ...this.business._extension,
          trusteeName: option.value,
          trusteeTaxId: option.abn,
        },
      };
      this.$refs.trusteeTaxId.setErrorMessage();

      this.setFocusTo(this.$refs.trusteeAnotherBusinessName.$refs.nativeInput);
    },
    onOptionChangeAcnTrustee(option) {
      this.business = {
        ...this.business,
        _extension: {
          ...this.business._extension,
          trusteeName: option.value,
          trusteeTaxId: option.acn,
        },
      };
      this.$refs.trusteeTaxId.setErrorMessage();

      this.setFocusTo(this.$refs.trusteeAnotherBusinessName.$refs.nativeInput);
    },
    onPlaceChange() {
      this.setFocusTo(this.$refs.businessPhone.$refs.maskedInput.$refs.input);
    },
  },
  mixins: [modalMixin],
};
</script>

<style lang="sass" scoped>
::v-deep
  .halved .control
    +mq($stego)
      width: 48.5%

.businessName,
.trusteeName
  ::v-deep
    .formControl
      margin-top: $glass-spacing-1

.combinedElement
  padding: $glass-spacing-2 $glass-spacing-3 $glass-spacing-4

.deepCombined
  background-color: $glass-color-system-neutral-grey-03

.descriptionLink
  font-weight: bold
  text-decoration: underline
  color: #000000

.popover
  vertical-align: super
  ::v-deep
    .icon
      color: glass-color-brand-primary-dark-03
    .content
      background: $glass-color-brand-secondary-accent-01
      z-index: 2
      color: #000000
    .iconButton
      &:focus
        outline: 0
        box-shadow: 0 0 0 4px $glass-color-brand-primary-light-02
</style>
